/**
 * The WIP stand beside the etcher's load ports: where lots wait.
 *
 * A load port holds one carrier. When lots arrive faster than a tool can
 * take them - which is what a tool losing two chambers of three does to
 * every lot on its way to etch - the extra carriers wait on a stand beside
 * the ports, and the stand is how a fab floor SHOWS a queue: a technician
 * walking the aisle reads the depth of trouble at a bay off how full its
 * stand is, before they have read a single screen.
 *
 * Six slots on three shelves, the carriers the same object as the ones on
 * the ports and in the hoists (builders.buildFoup). `setCount` shows that
 * many, bottom shelf first, the way a stand actually fills. Driven from the
 * live house state (FabScene.updateHouse): one staged lot on a healthy
 * line, six the moment the cooling water runs warm, because the line
 * simulation says that is what the trip does to etch (sim/line) and the
 * floor should say the same thing the numbers do.
 *
 * Stands on the bay wall past the tool's front end, outside the keep-clear
 * box - that box is the ground a carrier is handed over on and stays empty;
 * a stand in it would block the very hand-over the lots are waiting for.
 */

import * as THREE from 'three'
import { aisleDirection, toolFaceZ, toolSize, type Tool } from './layout'
import { buildFoup, type FabMaterials } from './builders'

export interface WipStand {
  group: THREE.Group
  /** Show this many waiting carriers, 0..6. */
  setCount(n: number): void
  /** Where the stand is, for a sound or a glance. */
  position: THREE.Vector3
}

const SLOTS = 6
const PER_SHELF = 2
const SHELF_Y = [0.34, 0.78, 1.22]
const SLOT_PITCH = 0.5

export function buildWipStand(mats: FabMaterials, tool: Tool): WipStand {
  const g = new THREE.Group()
  const facing = aisleDirection(tool)
  // Past the front end, on the bay wall beside the tool. The ports run the
  // other way, and beyond THEM stands the next bay's furniture - a first
  // cut put the stand through a wafer station. Past the HMI the wall is
  // clear up to the chase door, which is also where a technician coming
  // for a lot would look for it.
  const localX = -(toolSize(tool).w / 2 + 0.6)
  const x = tool.position[0] + (facing > 0 ? -localX : localX)
  const z = toolFaceZ(tool) + facing * 0.42
  g.position.set(x, 0, z)
  // Face the aisle: the stand's front is toward the walkway.
  g.rotation.y = facing > 0 ? 0 : Math.PI

  const frame = mats.steel
  const post = new THREE.BoxGeometry(0.03, 1.45, 0.03)
  const width = PER_SHELF * SLOT_PITCH + 0.06
  for (const sx of [-width / 2, width / 2]) {
    for (const sz of [-0.24, 0.24]) {
      const p = new THREE.Mesh(post, frame)
      p.position.set(sx, 0.725, sz)
      g.add(p)
    }
  }
  const shelf = new THREE.BoxGeometry(width, 0.02, 0.52)
  for (const y of SHELF_Y) {
    const s = new THREE.Mesh(shelf, frame)
    s.position.set(0, y - 0.01, 0)
    g.add(s)
  }
  // Feet: levelling pads, like every stand on this floor.
  const foot = new THREE.CylinderGeometry(0.03, 0.035, 0.02, 10)
  for (const sx of [-width / 2, width / 2]) {
    for (const sz of [-0.24, 0.24]) {
      const f = new THREE.Mesh(foot, mats.levellingPad)
      f.position.set(sx, 0.01, sz)
      g.add(f)
    }
  }

  const carriers: THREE.Group[] = []
  for (let i = 0; i < SLOTS; i++) {
    const c = buildFoup(mats)
    const shelfIndex = Math.floor(i / PER_SHELF)
    const col = i % PER_SHELF
    c.position.set((col - (PER_SHELF - 1) / 2) * SLOT_PITCH, SHELF_Y[shelfIndex] + 0.19, 0)
    c.visible = false
    g.add(c)
    carriers.push(c)
  }

  return {
    group: g,
    position: g.position,
    setCount(n: number): void {
      const k = Math.max(0, Math.min(SLOTS, Math.round(n)))
      carriers.forEach((c, i) => (c.visible = i < k))
    },
  }
}
